
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface RsvpItem {
  id: string;
  event_id: string;
  created_at: string;
}

export function useRsvp() {
  const { user } = useAuth();
  const [rsvps, setRsvps] = useState<RsvpItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadRsvps();
    } else {
      setRsvps([]);
      setLoading(false);
    }
  }, [user]);

  const loadRsvps = async () => {
    try {
      const { data, error } = await supabase
        .from('event_rsvps')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRsvps(data || []);
    } catch (error) {
      console.error('Error loading RSVPs:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasRsvp = (eventId: string) => {
    return rsvps.some((rsvp) => rsvp.event_id === eventId);
  };

  const addRsvp = async (eventId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase.from('event_rsvps').insert([
        {
          user_id: user.id,
          event_id: eventId,
        },
      ]);

      if (error) throw error;
      await loadRsvps();
      return true;
    } catch (error) {
      console.error('Error adding RSVP:', error);
      return false;
    }
  };

  const removeRsvp = async (eventId: string) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('event_rsvps')
        .delete()
        .eq('user_id', user.id)
        .eq('event_id', eventId);

      if (error) throw error;
      await loadRsvps();
      return true;
    } catch (error) {
      console.error('Error removing RSVP:', error);
      return false;
    }
  };

  const toggleRsvp = async (eventId: string) => {
    if (hasRsvp(eventId)) {
      return await removeRsvp(eventId);
    } else {
      return await addRsvp(eventId);
    }
  };

  return {
    rsvps,
    loading,
    hasRsvp,
    addRsvp,
    removeRsvp,
    toggleRsvp,
  };
}
